import ModuleLazyLoaderService from '../module-lazy-loader'
import PersistentStore from '../persistent-store'
import UtilService from '../util'
import siteConfig from '../../include/site.config.json'
import { useLocation } from 'react-router-dom'

const store = new PersistentStore()
let Q = null

const authUrl = 'https://s32lttlorlf5v5lmbgulpdqoiq0ecpjs.lambda-url.us-east-1.on.aws'

const storageKeys = {
    idToken: 'cognito:token:id_token',
//    incomingToken: 'cognito:token:incoming_token',
    initialEntityId: 'cognito:token:initial_entity_id',
}
let isDologout = false;

/**
 * Converts the Date.now() millisecond timestamp to a UNIX epoch as used in the JWT
 * @returns {integer} Current time epoch in seconds
 */
function getNowEpoch() {
    return Math.floor(Date.now() / 1000)
}

export default class CognitoService {

    static _refreshing = false

    static _loadModules() {
        const modules = [
            'q'
        ]
        return ModuleLazyLoaderService.load(modules)
            .then((modules) => {
                Q = modules['q']
                return modules
            })
    }


    /**
     * Checks the input token to see that it's valid. Or, at least, that it was valid at the
     * most recent point that we can check.
     * @static
     * @param {String} idToken (string version)
     * @returns {Promise<Boolean>}
     */
    static async _localValidateToken(idToken) {
        console.log(`_localValidateToken(${idToken})`)

        if (!idToken) {
            return false
        }

        const issuerUri = 'https://cognito-idp.us-east-1.amazonaws.com/us-east-1_bmaqiN6qw'
        const clientId = '5nbv3ddlrephm57er8rs5s542r'
      
        const modules = await ModuleLazyLoaderService.load('jwt-decode')
        const jwtDecode = modules['jwt-decode']

        try {
            const decodedIdToken = jwtDecode(idToken)
            const { exp, iss, aud } = decodedIdToken

            // First check that the incoming token has not expired more than 24 hours.
            // This is the maximum time that a token can be refreshed, so this protects us
            // against replay attacks (or just using old values).
            if (exp < (getNowEpoch() - 86400)) {
                console.log(`----- ID Token expired too long ago to refresh`)
                return false
            }
        
            // Check the token claims. If these don't match, it's not even our token
            // to begin with.
            if (iss !== issuerUri) {
                console.log(`----- Issuer - Expected: ${issuerUri}, got ${iss}`)
                return false
            }
      
            if (aud !== clientId) {
                console.log(`----- Audience - expected: ${clientId}, got ${aud}`)
                return false
            }
        
            return true
      
        } catch (error) {
          // We want to distinguish the case where the ID token is expired; it may be able to be refreshed
          if (error.name === 'TokenExpiredError') {
            console.log(`ID Token was expired`)
            return false
          }
      
          console.log('Error validating token:', error.message)
          return false
        }
    }


    static async getInitialEntityId() {
        const initialEntityId = await store.get(storageKeys.initialEntityId)
        await store.remove(storageKeys.initialEntityId)

        console.log(`Initial Entity ID: ${initialEntityId}`)
        if (initialEntityId == 'undefined') {
            return null
        }
        return initialEntityId
    }


    /**
     * Gets the token from storage. Called from OspreyApiHelper::authenticatedRequest()
     * @returns {Promise<String>} The access token from storage
     * @static
     */
    static async getToken() {
        return await store.get(storageKeys.idToken)
    }


    static async updateToken(newIdToken) {
        await store.set(storageKeys.idToken, newIdToken)
    }


    /**
     * Kick off a redirect to our custom login page to start a login.
     * Called from actions/auth::start().
     * @static
     * @returns {Promise<>}
     */
    static authenticate() {
        console.log(`Redirecting to login page`)

        window.location.href = `https://login.aiqauto.com` + (isDologout==false?'/?source=amp':'')
    }


    /**
     * Finish a login process that was started by authenticate().
     * Called from actions/auth::complete(). Stores the token
     * in browser memory.
     * 
     * This is also called from other Unified Login systems to 
     * pass the token (from their login ) and launch the application.
     * @static
     * @returns 
     */
    static async completeAuthentication() {
        console.log(`Completing Authentication`)

        /*
        // Access the token in localStorage where the Login 2 page
        // stores it from the query parameters.
        const token = localStorage.getItem(storageKeys.incomingToken)
        const entityId = localStorage.getItem(storageKeys.incomingEntityId)

        if (token) {
            await CognitoService.setSession({ idToken: token, entityId: entityId })
            console.log(`Token: ${token}, Entity ID: ${entityId}`)
        } else {
            console.error('Error: No token found')
        }
            */
    }


    /**
     * Returns the login key/value pairs for the auth parameters
     * @returns {array} Associative array of key/value pairs from storage
     * @static
     */
    static async getSession() {
        return {
            idToken: await store.get(storageKeys.idToken),
            initialEntityId: await store.get(storageKeys.initialEntityId)
        }
    }


    /**
     * Stores values from the authResult in storage.
     * @param {object} authResult
     * @static
     * @returns {object} Returns the input object
     */
    static async setSession(authResult) {
        await store.set(storageKeys.idToken, authResult.idToken)
        await store.set(storageKeys.initialEntityId, authResult.entityId)
    }


    /**
     * Removes all tokens from storage, and logs the user out from Cognito (so all
     * systems will see them as logged out). This should only be called due to explicit
     * user logout, or an authentication failure. Called from actions/auth::logout()
     * @static
     * @returns {Promise<*>}
     */
    static async logout() {
        const token = await CognitoService.getToken()
        localStorage.clear()
        sessionStorage.clear()
        isDologout = true;
        
        if (token) {
            await fetch(authUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'logout', token })
            })
        }
    }


    /**
     * 
     * @param {String} idType (e.g. ucid, etc.) Dealer type that was passed in.
     * @param {*} id 
     */
    static async normalizeDealerId(idType, id) {
        const messageBody = {
            action: 'normalize_dealer',
            idType: idType,
            dealerId: id
        }

        try {
            const result = await fetch(authUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(messageBody)
            })

            if (!result.ok) {
                throw new Error('Error normalizing dealer ID')
            }
        
            const data = await result.json()
            if (data.result != 'success') {
                throw new Error('Error normalizing dealer ID: ' + data.message)
            }

            return (data.entity_id)
        } catch (error) {
            console.error('Error normalizing dealer ID:', error)
        }
    }


    /**
     * Checks whether or not the user is still valid. We use only a local validation, which
     * is incomplete but is good enough for our purposes, as 1) it reflects the most recent
     * status that we know about and 2) will be corrected in the back-end auth if necessary.
     * @returns {Boolean}
     * @static
     */
    static async isAuthenticated() {
        if(location.pathname=='/login/gsm2'){
            // If this is the page to receive the token, set it up for access
            const hash = location.hash.substring(1) // Remove the #
            const params = new URLSearchParams(hash)
            const token = params.get('token')

            if (token) {
                localStorage.clear()
                sessionStorage.clear()

                // We may or may not have a dealer ID. If we do, it may have various
                // parameter names, depending on where it comes from. These will need
                // to be normalized to an entity ID.
                var entityId
                const ucid = params.get('ucid')
                const eid = params.get('eid')
                const legacyId = params.get('legacyId')
                if (ucid) {
                    entityId = await this.normalizeDealerId('ucid', ucid)
                } else if (eid) {
                    entityId = eid  // This is our ID; we use this for testing.
                } else if (legacyId) {
                    entityId = await this.normalizeDealerId('legacyId', legacyId)
                }
                await CognitoService.setSession({ idToken: token, entityId: entityId })
            }
        }
        return await CognitoService._localValidateToken(await CognitoService.getToken())
    }


    /**
     * Checks with the back-end to see if the user is still logged in.
     * @returns {Promise<Boolean>}
     * @static
     */
    static async checkLoginStatus() {
        const token = await CognitoService.getToken()
        if (!token) { return false }

        const result = await fetch(authUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ action: 'check_login_status', token })
        })

        if (!result.ok) { return false }
        
        const data = await result.json()
        return (data.status === 'logged_in')
    }
}


// WEBPACK FOOTER //
// src/services/auth/cognito.js